import { Loader2 } from "lucide-react"
import type { ReactNode } from "react"

export interface AsyncState<T> {
  readonly data?: T
  readonly error?: unknown
  readonly isPending: boolean
}

/** One place that decides what loading, failure and emptiness look like. */
export function Async<T>({
  state,
  children,
  empty,
}: {
  state: AsyncState<T>
  children: (data: NonNullable<T>) => ReactNode
  empty?: string
}) {
  if (state.isPending) {
    return (
      <p className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Wczytywanie…
      </p>
    )
  }

  // An error must not look like emptiness. On the health screen — whose whole
  // reason to exist is the moment something is broken — a failed fetch rendered
  // as muted "nothing to show" is precisely backwards, so it wears the risk
  // colour and a mark, while the empty state stays quiet and grey.
  if (state.error !== undefined && state.error !== null) {
    return (
      <p className="flex items-center justify-center gap-2 py-12 text-center font-medium text-critical">
        <span aria-hidden="true">!</span>
        {describe(state.error)}
      </p>
    )
  }

  if (state.data === undefined || state.data === null) {
    return (
      <p className="py-12 text-center text-muted-foreground">{empty ?? "Nie ma czego pokazać."}</p>
    )
  }

  return <>{children(state.data)}</>
}

function describe(error: unknown): string {
  if (typeof error === "object" && error !== null && "status" in error) {
    const status = (error as { status: number }).status
    if (status === 401) return "Sesja wygasła. Zaloguj się ponownie."
    if (status === 403) return "Nie masz do tego dostępu."
    if (status === 404) return "Nie znaleziono."
  }
  return error instanceof Error ? error.message : "Coś poszło nie tak."
}
